import React, { useState, useRef, useEffect, useCallback } from "react";
import Modal from "react-modal";
import config from "../configs/config";
import { useAuth } from "../providers/AuthProvider";
import { useToast } from "../contexts/ToastContext";
import CommonButton from "../elments/CommonButton";
import SingleResponsePreview from "./Survey/SingleResponsePreview";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { faRotateLeft, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { normalizeRequiredFlags } from "../utils/surveyNormalization";
import "../../src/components/styles/SurveyPreview.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import ISO6391 from "iso-639-1";
import {
  FaCode,
  FaCheckCircle,
  FaTable,
  FaList,
  FaCalculator,
  FaChartBar,
  FaThLarge,
  FaHome,
  FaClipboardList,
  FaInfoCircle,
  FaEdit,
  FaUserShield,
  FaTimes,
  FaSearch,
  FaCopy,
  FaChevronDown,
  FaChevronUp,
  FaQuestionCircle,
  FaListCheck,
  FaExclamationTriangle,
  FaFileAlt,
  FaUndo,
  FaRedo,
  FaMicrophone,
  FaStop,
  FaSave,
  FaEye,
  FaDownload,
  FaLanguage,
  FaRobot,
  FaComments,
  FaCheck,
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileCode,
} from "react-icons/fa";

const SurveyPreview = ({
  sdataGroup: initialSdataGroup,
  surveyName,
  surveyFileName,
  oeParameters,
  onShowToastMessage,
  onSetProcessingMessage,
  questionnaireFileId,
  exportedFiles = {},
  projectId,
  projectName,
  selectedClient,
  selectedProject,
  onSurveyUpdate,
  powner,
  pid,
  pname
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState("questions");
  const [sdataGroup, setSdataGroup] = useState(initialSdataGroup);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowedDownloadFeatures, setAllowedDownloadFeatures] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languageSearch, setLanguageSearch] = useState("");
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  // Per-question comments, master comment & AI review states
  const [comments, setComments] = useState({});
  const [masterComment, setMasterComment] = useState("");
  const [isSubmittingToAI, setIsSubmittingToAI] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [aiChanges, setAiChanges] = useState([]);
  const [aiSubmitMessage, setAiSubmitMessage] = useState(null);
  const [aiSubmitAttempts, setAiSubmitAttempts] = useState(0);

  // Editable Raw-JSON view state
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);

  // convert to array
  const languages = ISO6391.getAllCodes().map((code) => ({
    code,
    label: ISO6391.getName(code),
  }));

  const recognitionRef = useRef(null);
  const containerRef = useRef(null);

  // ========== UNDO / REDO (for live JSON editing in the preview) ==========
  const { undo, redo, canUndo, canRedo } = useUndoRedo({
    state: sdataGroup,
    setState: setSdataGroup,
    onRestore: (restored) => {
      if (previewType === "json") {
        setJsonText(JSON.stringify(restored ?? {}, null, 2));
        setJsonError(null);
      }
      if (onSurveyUpdate) onSurveyUpdate(restored);
      setHasUnsavedChanges(true);
    },
    resetKey: `${projectId}:${isPreviewOpen}`,
    debounceMs: 400,
    maxHistory: 100,
  });

  // Seed the editable JSON text whenever the Raw JSON view is opened.
  useEffect(() => {
    if (previewType === "json") {
      setJsonText(JSON.stringify(sdataGroup ?? {}, null, 2));
      setJsonError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewType]);

  // Live-edit handler for the Raw JSON textarea.
  const handleJsonEdit = (value) => {
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setJsonError(null);
      setSdataGroup(parsed);
      if (onSurveyUpdate) onSurveyUpdate(parsed);
      setHasUnsavedChanges(true);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  // Copy the full raw JSON to the clipboard.
  const [jsonCopied, setJsonCopied] = useState(false);
  const handleCopyJson = async () => {
    const text = jsonText || JSON.stringify(sdataGroup ?? {}, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 1500);
    } catch (err) {
      showToastMessage("Failed to copy JSON");
    }
  };

  // Undo/redo keyboard shortcuts
  useEffect(() => {
    if (!isPreviewOpen) return undefined;
    const handleKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewOpen, undo, redo]);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= (sdataGroup ? Object.keys(sdataGroup).length - 1 : 0);

  const handleNext = () => !isLast && setCurrentIndex((prev) => prev + 1);
  const handleBack = () => !isFirst && setCurrentIndex((prev) => prev - 1);

  const currentQuestion = sdataGroup ? Object.values(sdataGroup)[currentIndex] : null;

  // Sync sdataGroup when prop changes & initialise speech recognition
  useEffect(() => {
    setSdataGroup(initialSdataGroup);
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.onstart = () => setTranscript("Listening...");
      recognitionRef.current.onresult = (event) => {
        if (isSpeaking) return;
        const command = event.results[0][0].transcript.toLowerCase().trim();
        if (command.length < 3) return;
        setTranscript(command);
        handleVoiceCommand(command);
      };
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error !== "aborted" && event.error !== "not-allowed") {
          setTranscript(`Error: ${event.error}`);
          speak("Try again.");
        }
        setIsListening(false);
      };
      recognitionRef.current.onend = () => {
        setIsListening(false);
        setTranscript("");
      };
    }
  }, [initialSdataGroup, isSpeaking]);

  // Load DOCX preview when split view is enabled
  useEffect(() => {
    if (showSplitView && !embedUrl && questionnaireFileId) {
      loadDocxPreview();
    }
  }, [showSplitView]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization) {
      if (
        organization.download_features &&
        Array.isArray(organization.download_features) &&
        organization.download_features.length > 0
      ) {
        setAllowedDownloadFeatures(organization.download_features);
      } else {
        setAllowedDownloadFeatures([]);
      }
    } else {
      setAllowedDownloadFeatures([]);
    }
  }, [user]);

  const hasDownloadFeatureAccess = (feature) =>
    allowedDownloadFeatures.includes(feature);

  const showToastMessage = (message, type = 'info') => {
    if (onShowToastMessage) {
      onShowToastMessage(message);
    } else {
      const fn = toast[type] ?? toast.info;
      fn(message);
    }
  };

  const openSurveyPreview = () => {
    const angularBaseUrl = process.env.REACT_APP_SURVEY_PREVIEW_URL || 'http://localhost:8100';
    if (!pid) {
      alert("PID not available. Please save the survey first.");
      return;
    }
    window.open(`${angularBaseUrl}/survey/${pid}`, '_blank');
  };

  const setProcessingMessage = (message) => {
    if (onSetProcessingMessage) onSetProcessingMessage(message);
  };

  // ─── Document preview ───────────────────────────────────────────────────────
  const loadDocxPreview = async () => {
    if (!questionnaireFileId) {
      showToastMessage("No document available for preview");
      return;
    }
    try {
      setIsLoadingDocx(true);
      setProcessingMessage("Loading document preview...");
      const response = await fetch(`${config.API_BASE_URL}/get_embed_url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          onedrive_file_id: questionnaireFileId,
          surveyFileName,
          powner,
        }),
      });
      if (!response.ok) throw new Error("Failed to get embed URL");
      const data = await response.json();
      if (data.success) {
        setEmbedUrl(data.embed_url);
      } else {
        showToastMessage("Failed to load document preview");
        setShowSplitView(false);
      }
    } catch (error) {
      console.error("Error previewing document:", error);
      showToastMessage("Error loading document preview");
      setShowSplitView(false);
    } finally {
      setIsLoadingDocx(false);
    }
  };

  // ─── AI review: submit questionnaire + comments ──────────────────────────────
  const handleSubmitToAI = async () => {
    if (aiSubmitAttempts >= 3) {
      setAiSubmitMessage({ success: false, text: "Maximum 3 attempts reached for this session. Please close and reopen the preview to reset." });
      return;
    }

    if (aiSubmitAttempts === 0) {
      const proceed = window.confirm(
        "Attempt 1 of 3\n\nHave you completed all your review comments? AI produces the best results when you send all your feedback in one comprehensive batch.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    } else if (aiSubmitAttempts === 1) {
      const proceed = window.confirm(
        "Attempt 2 of 3\n\nThis is your second submission. Make sure you have added all remaining comments before proceeding — you have only 1 attempt left after this.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    }

    try {
      setIsSubmittingToAI(true);
      setAiSubmitMessage(null);

      const reviewItems = Object.entries(sdataGroup)
        .filter(([key]) => comments[key]?.trim())
        .map(([key, question]) => ({
          key,
          question,
          comment: comments[key].trim(),
        }));

      const allQuestions = Object.entries(sdataGroup)
        .map(([key, q]) => ({
          key,
          Question_number: q.Question_number || key,
          type: q.type || "",
          _order: q._order || 0,
          title_short: (q.Qtitle || "").replace(/<[^>]+>/g, "").substring(0, 60),
        }))
        .sort((a, b) => a._order - b._order);

      const response = await fetch(
        `${config.API_BASE_URL}/api/ai/review-with-comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            reviewItems,
            masterComment: masterComment.trim(),
            allQuestions,
            surveyFileName,
            questionnaireFileId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from AI");
      }

      const data = await response.json();
      const changes = [];

      if (data.updatedQuestions) {
        Object.entries(data.updatedQuestions).forEach(([key, updated]) => {
          changes.push({
            questionKey: key,
            original: sdataGroup[key] || null,
            updated,
            isNew: false,
            status: "pending",
          });
        });
      }

      if (data.newQuestions?.length > 0) {
        data.newQuestions.forEach((nq, idx) => {
          const q = nq.question || {};
          const qKey = q.Question_number || `NEW_Q_${idx + 1}`;
          changes.push({
            questionKey: qKey,
            original: null,
            updated: q,
            isNew: true,
            insertAfterKey: nq.insertAfter || null,
            insertBeforeKey: nq.insertBefore || null,
            status: "pending",
          });
        });
      }

      if (changes.length > 0) {
        setAiChanges(changes);
        setShowReviewModal(true);
      } else {
        setAiSubmitMessage({
          success: false,
          text: data.message || "AI processed the request but no changes were proposed.",
        });
      }
      setAiSubmitAttempts((prev) => prev + 1);
    } catch (error) {
      console.error("AI submit error:", error);
      setAiSubmitMessage({ success: false, text: `Error: ${error.message}` });
      setAiSubmitAttempts((prev) => prev + 1);
    } finally {
      setIsSubmittingToAI(false);
    }
  };

  // ─── AI review modal: accept / reject helpers ────────────────────────────────
  const acceptChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "accepted" } : c
      )
    );

  const rejectChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "rejected" } : c
      )
    );

  const acceptAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "accepted" })));

  const rejectAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "rejected" })));

  const applySelectedChanges = () => {
    const accepted = aiChanges.filter((c) => c.status === "accepted");
    if (accepted.length > 0) {
      const newSdataGroup = { ...sdataGroup };
      let deletedCount = 0;
      let modifiedCount = 0;
      let newCount = 0;

      accepted.forEach(({ questionKey, updated: upd, isNew }) => {
        if (upd?._deleted) {
          delete newSdataGroup[questionKey];
          deletedCount++;
        } else if (isNew) {
          const finalKey = upd.Question_number || questionKey;
          newSdataGroup[finalKey] = upd;
          newCount++;
        } else {
          newSdataGroup[questionKey] = upd;
          modifiedCount++;
        }
      });

      setSdataGroup(newSdataGroup);
      if (onSurveyUpdate) onSurveyUpdate(newSdataGroup);
      setHasUnsavedChanges(true);

      const parts = [];
      if (modifiedCount > 0) parts.push(`${modifiedCount} modified`);
      if (deletedCount > 0) parts.push(`${deletedCount} deleted`);
      if (newCount > 0) parts.push(`${newCount} added`);

      setAiSubmitMessage({
        success: true,
        text: `Applied AI changes: ${parts.join(", ")} successfully.`,
      });
      setComments({});
      setMasterComment("");
    }
    setShowReviewModal(false);
    setAiChanges([]);
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang)
        ? prev.filter((l) => l !== lang)
        : [...prev, lang]
    );
  };

  const downloadexcelfile = async (selectedLanguages) => {
    setIsDownloadingExcel(true);
    try {

      let data =
        typeof sdataGroup === "string"
          ? JSON.parse(sdataGroup)
          : sdataGroup;

      // ? convert object to array
      if (!Array.isArray(data)) {
        data = Object.values(data);
      }

      console.log("FINAL DATA:", data);

      const excelRows = [];

      // =========================================
      // STEP 1: EXTRACT ALL TEXTS
      // =========================================
      const baseTexts = [];

      data.forEach((q) => {

        if (q.Qtitle) {
          baseTexts.push(q.Qtitle);
        }

        q.rows?.forEach((row) => {
          baseTexts.push(`${row.index}. ${row.text}`);
        });

        q.columns?.forEach((col) => {
          baseTexts.push(`${col.index}. ${col.text}`);
        });

      });

      // =========================================
      // STEP 2: TRANSLATION API
      // =========================================
      const response = await axios.post(
        `${config.API_BASE_URL}/translate`,
        {
          texts: baseTexts,
          languages: selectedLanguages,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const translatedData = response.data;

      console.log("TRANSLATED:", translatedData);

      // =========================================
      // STEP 3: CREATE TRANSLATION MAP
      // =========================================
      const translationMap = {};

      translatedData.forEach((item) => {
        translationMap[item.original] = item.translations;
      });

      // =========================================
      // STEP 4: HEADERS
      // =========================================

      // ? DEFAULT ENGLISH COLUMN FIRST
      const headers = [
        "Parent Type",
        "Question Code",
        "Type",
        "Code",
        "Field_Default",

        // translated columns after default
        ...selectedLanguages.map(
          (lang) => `Field_${lang}`
        ),
      ];

      // =========================================
      // STEP 5: CREATE LANGUAGE OBJECT
      // =========================================
      const createLanguageObject = (text = "") => {

        const obj = {};

        const translations = translationMap[text] || {};

        // ? FIRST DEFAULT TEXT
        obj["Field_Default"] = text;

        // ? THEN TRANSLATED TEXTS
        selectedLanguages.forEach((lang) => {

          obj[`Field_${lang}`] = translations[lang] || text;

        });

        return obj;
      };

      // =========================================
      // STEP 6: BUILD EXCEL ROWS
      // =========================================
      data.forEach((q) => {

        const qCode = q.Question_number;

        // =====================================
        // INTRO
        // =====================================
        if (q.type === "Intro") {

          excelRows.push({
            "Parent Type": "Question",
            "Question Code": "",
            "Type": `${qCode}Repeat`,
            "Code": "",
            ...createLanguageObject(q.Qtitle),
          });

        } else {

          // =================================
          // QUESTION TITLE
          // =================================
          excelRows.push({
            "Parent Type": "Question",
            "Question Code": qCode,
            "Type": "Text",
            "Code": "",
            ...createLanguageObject(q.Qtitle),
          });

        }

        // =====================================
        // ROWS
        // =====================================
        q.rows?.forEach((row) => {

          const text = `${row.index}. ${row.text}`;

          excelRows.push({
            "Parent Type": "Question",
            "Question Code": qCode,
            "Type": "Rows",
            "Code": row.index,
            ...createLanguageObject(text),
          });

        });

        // =====================================
        // COLUMNS
        // =====================================
        q.columns?.forEach((col) => {

          const text = `${col.index}. ${col.text}`;

          excelRows.push({
            "Parent Type": "Question",
            "Question Code": qCode,
            "Type": "Column",
            "Code": col.index,
            ...createLanguageObject(text),
          });

        });

      });

      console.log("EXCEL ROWS:", excelRows);

      // =========================================
      // STEP 7: CREATE EXCEL
      // =========================================
      const worksheet = XLSX.utils.json_to_sheet(
        excelRows,
        {
          header: headers,
          skipHeader: false,
        }
      );

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Survey"
      );

      XLSX.writeFile(
        workbook,
        "Survey.xlsx"
      );

      setShowPopup(false); // ✅ close only after success
    } catch (err) {
      console.error("Error:", err);
      showToastMessage("Failed to generate Excel file");
      // popup open hi rehne do taaki user retry kar sake ya dekh sake kya hua
    } finally {
      setIsDownloadingExcel(false);
    }
  };


  const filteredLanguages = languages.filter((lang) =>
    lang.label.toLowerCase().includes(languageSearch.toLowerCase())
  );

  // ─── Save survey ─────────────────────────────────────────────────────────────
  const handleSaveSurvey = async () => {
    if (!projectId) {
      showToastMessage("No project ID available");
      return;
    }
    try {
      setIsSaving(true);
      setProcessingMessage("Saving survey...");
      const normalizedData = normalizeRequiredFlags(sdataGroup);
      setSdataGroup(normalizedData);

      const response = await fetch(`${config.API_BASE_URL}/save-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          sdataGroup: normalizedData,
          client: JSON.stringify(selectedClient),
          project: JSON.stringify(selectedProject),
        }),
      });
      if (!response.ok) throw new Error("Failed to save survey");
      await response.json();
      showToastMessage("Survey saved successfully!");
      setHasUnsavedChanges(false);
      if (onSurveyUpdate) onSurveyUpdate(normalizedData);
      setProcessingMessage("");
    } catch (error) {
      console.error("Error saving survey:", error);
      showToastMessage("Failed to save survey");
    } finally {
      setIsSaving(false);
      setProcessingMessage("");
    }
  };

  const handlePreviewChange = (value) => {
    if (value === "psnst") {
      openSurveyPreview();
      return;
    }
    if (value === "translation") {
      setSelectedLanguages([]);
      setLanguageSearch("");
      setShowPopup(true);
      return;
    }
    if (value.startsWith("export_")) {
      setSelectedExport(value.replace("export_", ""));
      setPreviewType("export");
    } else {
      setSelectedExport(null);
      setPreviewType(value);
    }
  };

  const toggleSplitView = () => {
    if (!questionnaireFileId) {
      showToastMessage("No document file available for this survey");
      return;
    }
    setShowSplitView(!showSplitView);
  };

  // ─── DOCX divider drag ───────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    },
    [isDragging]
  );

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // ─── Voice assistant ─────────────────────────────────────────────────────────
  const speak = (text) => {
    if ("speechSynthesis" in window && !isSpeaking) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.2;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    }
  };

  const handleVoiceCommand = (command) => {
    if (isSpeaking) return;
    let updated = false;
    const qMatch =
      command.match(/question (\d+(?:\.\d+)?)/i) ||
      command.match(/q(\d+(?:\.\d+)?)/i);
    const questionNum = qMatch ? parseInt(qMatch[1]) : null;
    const questionKey = questionNum
      ? questionNum.toString()
      : Object.keys(sdataGroup)[0];

    if (!sdataGroup || !sdataGroup[questionKey]) {
      speak("Question not found.");
      return;
    }

    const isBold = /bold|emphasize|strong/i.test(command);
    const isUnbold = /unbold|remove bold|normal/i.test(command);
    const targetTitle = /title|question text/i.test(command);
    const targetRow = command.match(/(?:row|option)s?\s*(\d+(?:\.\d+)?)/i);
    const targetCol = command.match(/(?:column|col)s?\s*(\d+(?:\.\d+)?)/i);
    const targetStmt = command.match(/(?:statement|item)s?\s*(\d+(?:\.\d+)?)/i);

    let target = null;
    let index = null;
    let subKey = null;

    if (targetTitle) {
      target = "Qtitle";
    } else if (targetRow) {
      const rowNum = parseInt(targetRow[1]) - 1;
      if (
        sdataGroup[questionKey]?.rows &&
        rowNum >= 0 &&
        rowNum < sdataGroup[questionKey].rows.length
      ) {
        target = "rows";
        index = rowNum;
        subKey = "text";
      } else {
        speak(`Row ${targetRow[1]} not found.`);
        return;
      }
    } else if (targetCol) {
      const colNum = parseInt(targetCol[1]) - 1;
      if (
        sdataGroup[questionKey]?.columns &&
        colNum >= 0 &&
        colNum < sdataGroup[questionKey].columns.length
      ) {
        target = "columns";
        index = colNum;
        subKey = "text";
      } else {
        speak(`Column ${targetCol[1]} not found.`);
        return;
      }
    } else if (targetStmt) {
      const stmtNum = parseInt(targetStmt[1]) - 1;
      if (
        sdataGroup[questionKey]?.statements &&
        stmtNum >= 0 &&
        stmtNum < sdataGroup[questionKey].statements.length
      ) {
        target = "statements";
        index = stmtNum;
        subKey = "text";
      } else {
        speak(`Statement ${targetStmt[1]} not found.`);
        return;
      }
    }

    if (target && (isBold || isUnbold)) {
      if (isBold) {
        updated = updateTextBold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`} bolded.`
        );
      } else if (isUnbold) {
        updated = updateTextUnbold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`
          } unbolded.`
        );
      }
    }

    if (!updated && command.length > 5 && !/stop|exit|cancel/i.test(command)) {
      setTimeout(() => {
        speak("Command not recognized. Try 'bold title' or 'unbold row two'.");
      }, 300);
    }

    if (updated) setSdataGroup((prev) => ({ ...prev }));
  };

  const updateTextBold = (questionKey, target, index = null, subKey = null) => {
    const toggleBold = (text) => {
      if (typeof text !== "string") return text;
      const boldRegex = /^<strong>\s*(.*?)\s*<\/strong>$/i;
      return boldRegex.test(text)
        ? text.replace(boldRegex, "$1").trim()
        : `<strong>${text}</strong>`;
    };
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = toggleBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = toggleBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const updateTextUnbold = (
    questionKey,
    target,
    index = null,
    subKey = null
  ) => {
    const stripBold = (text) =>
      typeof text !== "string"
        ? text
        : text.replace(/<strong>\s*(.*?)\s*<\/strong>/gi, "$1");
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = stripBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = stripBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      speak("Voice not supported.");
      return;
    }
    speechSynthesis.cancel();
    setIsSpeaking(false);
    if (isListening) {
      recognitionRef.current.stop();
      setTranscript("");
    } else {
      setTranscript("Ready...");
      recognitionRef.current.start();
    }
    setIsListening(!isListening);
    if (!isSpeaking) speak(isListening ? "Stopped." : "Say your command now.");
  };

  // ─── Row / column setting badges ──────────────────────────────────────────────
  const getRowSettingLabels = (row) => {
    const labels = [];
    if (row.screenout)
      labels.push({ text: "Screen Out", color: "#dc2626", bgColor: "#fecaca" });
    if (row.other)
      labels.push({ text: "Other", color: "#7c3aed", bgColor: "#ddd6fe" });
    if (row.otherPiping)
      labels.push({
        text: `Piping ${row.otherPiping}`,
        color: "#0369a1",
        bgColor: "#bae6fd",
      });
    if (row.filterLogic)
      labels.push({
        text: `Logic: ${row.filterLogic}`,
        color: "#ea580c",
        bgColor: "#fed7aa",
      });
    if (row.randomize)
      labels.push({
        text: "Randomize",
        color: "#059669",
        bgColor: "#a7f3d0",
      });
    if (row.exclusive)
      labels.push({
        text: "Exclusive",
        color: "#d97706",
        bgColor: "#fde68a",
      });
    if (row.hidden)
      labels.push({ text: "Hidden", color: "#4b5563", bgColor: "#d1d5db" });
    if (row.anchor)
      labels.push({ text: "Anchor", color: "#65a30d", bgColor: "#d9f99d" });
    if (row.rotationGroup)
      labels.push({
        text: `Rotation: ${row.rotationGroup}`,
        color: "#be185d",
        bgColor: "#fbcfe8",
      });
    if (row.fix)
      labels.push({ text: "Fix", color: "#4338ca", bgColor: "#c7d2fe" });
    if (row.header)
      labels.push({ text: "Header", color: "#0f766e", bgColor: "#99f6e4" });
    if (row.settings) {
      Object.entries(row.settings).forEach(([key, value]) => {
        if (
          value !== false &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          labels.push({
            text: `${key}: ${value}`,
            color: "#6b7280",
            bgColor: "#e5e7eb",
          });
        }
      });
    }
    return labels;
  };

  const badgeStyle = (bg, color) => ({
    fontSize: "11px",
    padding: "3px 10px",
    backgroundColor: bg,
    color,
    borderRadius: "4px",
    fontWeight: 600,
    display: "inline-block",
  });

  const renderRowBadges = (row) => {
    if (!row) return null;
    const badges = [];
    if (row.screenout)
      badges.push(
        <span key="so" style={badgeStyle("#fee2e2", "#991b1b")}>
          Screen Out
        </span>
      );
    if (row.exclusive)
      badges.push(
        <span key="ex" style={badgeStyle("#dbeafe", "#1e40af")}>
          Exclusive
        </span>
      );
    if (row.other)
      badges.push(
        <span key="ot" style={badgeStyle("#fef3c7", "#92400e")}>
          Other (specify)
        </span>
      );
    if (row.fix)
      badges.push(
        <span key="fx" style={badgeStyle("#e0e7ff", "#3730a3")}>
          Fixed
        </span>
      );
    if (row.quotaCounts)
      badges.push(
        <span key="qt" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Quota: {row.quotaCounts}
        </span>
      );
    if (row.punchLogic)
      badges.push(
        <span key="pl" style={badgeStyle("#ecfdf5", "#065f46")}>
          Punch: {row.punchLogic}
        </span>
      );
    if (row.filterLogic)
      badges.push(
        <span key="fl" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Filter: {row.filterLogic}
        </span>
      );
    return badges.length > 0 ? (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginTop: "6px",
        }}
      >
        {badges}
      </div>
    ) : null;
  };

  // ─── Respondent (survey) view ─────────────────────────────────────────────────
  const renderSurveyView = (q) => {
    if (!q)
      return (
        <div style={{ color: "#6b7280", padding: "2rem", textAlign: "center" }}>
          No question data available
        </div>
      );

    const titleHtml = { __html: q.Qtitle || "No question text" };
    const type = q.type || "Unknown";

    if (type === "Intro") {
      return (
        <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
          <div
            style={{ fontSize: "20px", marginBottom: "2rem", color: "#1f2937" }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <button
            style={{
              padding: "14px 48px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "default",
              transition: "background-color 0.2s"
            }}
          >
            CONTINUE
          </button>
        </div>
      );
    }

    if (type === "Single Response" && q.rows?.length > 0) {
      return <SingleResponsePreview question={q} />;
    }

    if (type === "Numeric") {
      const oe = q.oeparameters || {};
      return (
        <div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#1f2937"
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <input
            type="number"
            min={oe.min}
            max={oe.max}
            placeholder="Enter your answer"
            readOnly
            style={{
              width: "200px",
              padding: "12px 16px",
              fontSize: "16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
              color: "#6b7280"
            }}
          />
        </div>
      );
    }

    if (q.rows?.length > 0 && q.columns?.length > 0) {
      const isMulti = type.includes("Multi") || type === "Multi Grid";
      return (
        <div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#1f2937"
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "800px",
                fontSize: "14px"
              }}
            >
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "left",
                      border: "1px solid #e2e8f0",
                      minWidth: "300px",
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#334155"
                    }}
                  >
                    Statement / Item
                  </th>
                  {q.columns.map((col) => (
                    <th
                      key={col.index}
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                        border: "1px solid #e2e8f0",
                        minWidth: "120px",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#334155",
                        whiteSpace: "normal",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: col.text || "—" }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.rows.map((row, i) => (
                  <tr
                    key={row.index || i}
                    style={{
                      background: i % 2 === 0 ? "#ffffff" : "#f8fafc",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        border: "1px solid #e2e8f0",
                        fontSize: "14px",
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          marginBottom: "6px",
                        }}
                        dangerouslySetInnerHTML={{ __html: row.text || "—" }}
                      />
                      {renderRowBadges(row)}
                    </td>
                    {q.columns.map((col) => (
                      <td
                        key={col.index}
                        style={{
                          textAlign: "center",
                          padding: "12px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <input
                          type={isMulti ? "checkbox" : "radio"}
                          readOnly
                          style={{ width: "18px", height: "18px", cursor: "default" }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: "#6b7280", padding: "2rem 0" }}>
        <div
          style={{ fontSize: "20px", color: "#1f2937" }}
          dangerouslySetInnerHTML={titleHtml}
        />
        <p style={{ marginTop: "1rem", fontSize: "14px" }}>
          (No preview available for type: {type})
        </p>
      </div>
    );
  };

  // ─── Export preview ───────────────────────────────────────────────────────────
  const renderExportPreview = () => {
    if (!selectedExport || !exportedFiles[selectedExport]) {
      return (
        <div className="preview-empty">
          <FaFileAlt size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
          <h4>No export selected</h4>
          <p>Select an export from the dropdown above to view its content</p>
        </div>
      );
    }
    const exportData = exportedFiles[selectedExport];
    return (
      <div className="export-preview-container">
        <div className="export-preview-header">
          <div>
            <h4>{exportData.fileName}</h4>
            <p>Exported: {new Date(exportData.timestamp).toLocaleString()}</p>
          </div>
          <button
            className="action-button primary"
            onClick={() => {
              const blob = new Blob([exportData.content], {
                type: "text/plain",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = exportData.fileName;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <FaDownload /> Download
          </button>
        </div>
        <div className="export-preview-body">
          <pre>{exportData.content}</pre>
        </div>
      </div>
    );
  };

  // ─── JSON preview ─────────────────────────────────────────────────────────────
  const renderJSONPreview = () => (
    <div className="preview-json">
      <div className="json-editor-header">
        <span className="hint">
          <FaEdit style={{ marginRight: "6px" }} />
          Edit the survey JSON directly — changes apply live. Use Undo / Redo
          (Ctrl+Z / Ctrl+Y) to step through your edits.
        </span>
        <div className="status">
          {jsonError ? (
            <span className="invalid">
              ⚠ Invalid JSON — changes not applied
            </span>
          ) : (
            <span className="valid">
              <FaCheckCircle style={{ marginRight: "4px" }} />
              Valid JSON
            </span>
          )}
          <button
            type="button"
            onClick={handleCopyJson}
            className={`json-copy-btn ${jsonCopied ? "copied" : ""}`}
          >
            {jsonCopied ? "✓ Copied" : "⧉ Copy JSON"}
          </button>
        </div>
      </div>
      <textarea
        className={`json-textarea ${jsonError ? "error" : ""}`}
        value={jsonText}
        onChange={(e) => handleJsonEdit(e.target.value)}
        spellCheck={false}
        wrap="soft"
      />
    </div>
  );

  // ─── Statistics preview ───────────────────────────────────────────────────────
  const renderStatisticsPreview = () => {
    if (!sdataGroup) return <div className="preview-empty">No data available</div>;

    const totalQuestions = Object.keys(sdataGroup).length;
    const questionTypes = {};
    let totalRows = 0;
    let totalColumns = 0;
    let totalStatements = 0;
    let rowsWithSettings = 0;
    let columnsWithSettings = 0;

    Object.values(sdataGroup).forEach((question) => {
      questionTypes[question.type] = (questionTypes[question.type] || 0) + 1;
      totalRows += (question.rows || []).length;
      totalColumns += (question.columns || []).length;
      totalStatements += (question.statements || []).length;
      if (question.rows)
        question.rows.forEach((row) => {
          if (getRowSettingLabels(row).length > 0) rowsWithSettings++;
        });
      if (question.columns)
        question.columns.forEach((col) => {
          if (getRowSettingLabels(col).length > 0) columnsWithSettings++;
        });
    });

    return (
      <div className="preview-stats">
        <h3><FaChartBar style={{ marginRight: "10px" }} />Survey Statistics</h3>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value blue">{totalQuestions}</div>
            <div className="stat-label">Total Questions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalRows}</div>
            <div className="stat-label">Total Rows</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalColumns}</div>
            <div className="stat-label">Total Columns</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalStatements}</div>
            <div className="stat-label">Total Statements</div>
          </div>
          <div className="stat-card">
            <div className="stat-value purple">{rowsWithSettings}</div>
            <div className="stat-label">Rows with Settings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value purple">{columnsWithSettings}</div>
            <div className="stat-label">Columns with Settings</div>
          </div>
        </div>

        <div className="question-types-section">
          <h4><FaList style={{ marginRight: "8px" }} />Question Types Distribution</h4>
          <div className="question-types-grid">
            {Object.entries(questionTypes)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="type-item">
                  <span className="type-name">{type}</span>
                  <span className="type-count">{count}</span>
                </div>
              ))}
          </div>
          {Object.keys(questionTypes).length === 0 && (
            <p className="no-questions">No question types found</p>
          )}
        </div>
      </div>
    );
  };

  // Most oe params are only worth displaying when truthy/non-empty
  const isDisplayableOeParam = ([key, value]) => {
    if (key === "isRequired") {
      return typeof value === "boolean";
    }
    return value !== false && value !== null && value !== undefined && value !== "";
  };

  // ─── Questions preview (with comment boxes) ───────────────────────────────────
  const renderQuestionsPreview = () => {
    if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
      return <div className="preview-empty">
        <FaClipboardList size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
        <h4>No questions available</h4>
        <p>Add questions to your survey to preview them here</p>
      </div>;
    }

    const sortedEntries = Object.entries(sdataGroup).sort((a, b) => {
      return (a[1]._order || 0) - (b[1]._order || 0);
    });

    const hasAnyComment =
      Object.values(comments).some((c) => c?.trim()) || masterComment.trim().length > 0;

    return (
      <div>
        {sortedEntries.map(([questionNumber, question]) => (
          <div key={questionNumber} className="preview-question">
            <div className="preview-question-header">
              <span className="preview-qnumber">
                <FaQuestionCircle style={{ marginRight: "6px", color: "#2563eb" }} />
                {question.Question_number || questionNumber}
              </span>
              <span className="preview-qtype">{question.type}</span>
            </div>

            <div
              className="preview-qtitle"
              dangerouslySetInnerHTML={{ __html: question.Qtitle || "No title" }}
            />

            {question.instructions && (
              <div className="preview-instructions">
                <strong><FaInfoCircle style={{ marginRight: "4px" }} />Instructions:</strong>
                <span
                  dangerouslySetInnerHTML={{ __html: question.instructions }}
                />
              </div>
            )}

            {question.logics && (
              <div className="preview-logic">
                <strong><FaExclamationTriangle style={{ marginRight: "4px" }} />Logic:</strong> {question.logics}
              </div>
            )}

            {question.rows && question.rows.length > 0 && (
              <div className="preview-rows">
                <strong><FaList style={{ marginRight: "4px" }} />Rows ({question.rows.length}):</strong>
                <ul>
                  {question.rows.map((row, index) => {
                    const settingLabels = getRowSettingLabels(row);
                    return (
                      <li key={index} className="preview-row-item">
                        <div className="row-content">
                          <span className="row-index">
                            {row.index || index + 1}
                          </span>
                          <div className="row-text-and-settings">
                            <span
                              className="row-text"
                              dangerouslySetInnerHTML={{ __html: row.text }}
                            />
                            {settingLabels.length > 0 && (
                              <div className="row-settings-inline">
                                {settingLabels.map((label, li) => (
                                  <span
                                    key={li}
                                    className="row-setting-inline"
                                    style={{
                                      color: label.color,
                                      backgroundColor: label.bgColor,
                                      borderColor: label.color,
                                    }}
                                  >
                                    {label.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {question.columns && question.columns.length > 0 && (
              <div className="preview-columns">
                <strong><FaThLarge style={{ marginRight: "4px" }} />Columns ({question.columns.length}):</strong>
                <div className="preview-columns-list">
                  {question.columns.map((col, index) => {
                    const settingLabels = getRowSettingLabels(col);
                    return (
                      <div key={index} className="preview-column-item">
                        <span className="column-index">
                          {col.index || index + 1}.
                        </span>
                        <div className="column-text-and-settings">
                          <span
                            className="column-text"
                            dangerouslySetInnerHTML={{ __html: col.text }}
                          />
                          {settingLabels.length > 0 && (
                            <div className="column-settings-inline">
                              {settingLabels.map((label, li) => (
                                <span
                                  key={li}
                                  className="column-setting-inline"
                                  style={{
                                    color: label.color,
                                    backgroundColor: label.bgColor,
                                    borderColor: label.color,
                                  }}
                                >
                                  {label.text}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {question.statements && question.statements.length > 0 && (
              <div className="preview-statements">
                <strong><FaClipboardList style={{ marginRight: "4px" }} />Statements ({question.statements.length}):</strong>
                <ul>
                  {question.statements.map((stmt, index) => (
                    <li
                      key={index}
                      dangerouslySetInnerHTML={{ __html: stmt.text }}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="preview-settings">
              {question.rowsRandomisation && (
                <span className="setting-badge rows-random">Rows Random</span>
              )}
              {question.columnsRandomisation && (
                <span className="setting-badge cols-random">Cols Random</span>
              )}
              {question.rowsRotation && (
                <span className="setting-badge rows-rotate">Rows Rotate</span>
              )}
              {question.columnsRotation && (
                <span className="setting-badge cols-rotate">Cols Rotate</span>
              )}
              {question.alphabeticalOrder && (
                <span className="setting-badge az-order">A-Z Order</span>
              )}
              {question.columnIndex && (
                <span className="setting-badge col-index">Col Index</span>
              )}
            </div>

            {question.oeparameters &&
              Object.entries(question.oeparameters).filter(isDisplayableOeParam).length > 0 && (
                <div className="preview-section">
                  <label><FaCalculator style={{ marginRight: "4px" }} />Parameters:</label>
                  <div className="preview-oe-params">
                    {Object.entries(question.oeparameters)
                      .filter(isDisplayableOeParam)
                      .map(([key, value]) => (
                        <div key={key} className="oe-param-item">
                          <span className="param-key">{key}:</span>
                          <span className="param-value">{String(value)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* ── Per-question comment for AI ── */}
            <div className="comment-box">
              <label><FaComments style={{ marginRight: "4px" }} />Comment for AI:</label>
              <textarea
                className={comments[questionNumber]?.trim() ? "has-comment" : ""}
                value={comments[questionNumber] || ""}
                onChange={(e) =>
                  setComments((prev) => ({
                    ...prev,
                    [questionNumber]: e.target.value,
                  }))
                }
                placeholder="Describe what you'd like AI to change or review for this question..."
                rows={2}
              />
            </div>
          </div>
        ))}

        {/* ── Master comment box ── */}
        <div className={`master-comment-box ${masterComment.trim() ? "has-comment" : ""}`}>
          <div className="header">
            <span>🧠</span>
            <label>Master Instruction for AI (applies to entire survey)</label>
          </div>
          <textarea
            className={masterComment.trim() ? "has-comment" : ""}
            value={masterComment}
            onChange={(e) => setMasterComment(e.target.value)}
            placeholder={
              "Examples:\n" +
              "• Add hidden quota questions that are missing for Q3 and Q5\n" +
              "• Add a screening termination question before Q1\n" +
              "• All grid questions should have columnsRandomisation: true\n" +
              "• Create a HIDDEN derived variable after Q8 that codes age groups"
            }
            rows={5}
          />
          <p className="hint">
            This box sends ONE batch call to AI with a compact survey summary — token-efficient for survey-wide changes.
          </p>
        </div>

        {/* ── Submit to AI section ── */}
        <div className="ai-submit-section">
          {aiSubmitMessage && (
            <div className={`ai-submit-message ${aiSubmitMessage.success ? "success" : "error"}`}>
              {aiSubmitMessage.text}
            </div>
          )}

          <button
            className="ai-submit-btn"
            onClick={handleSubmitToAI}
            disabled={isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3}
            title={
              aiSubmitAttempts >= 3
                ? "Maximum 3 attempts reached — close and reopen the preview to reset"
                : !hasAnyComment
                  ? "Add a per-question comment or a master instruction before submitting"
                  : `Submit all comments to AI (attempt ${aiSubmitAttempts + 1} of 3)`
            }
          >
            {isSubmittingToAI
              ? <><FaRobot style={{ marginRight: "8px" }} />Submitting to AI…</>
              : aiSubmitAttempts >= 3
                ? "Max Attempts Reached"
                : <><FaRobot style={{ marginRight: "8px" }} />Submit to AI ({aiSubmitAttempts + 1}/3)</>}
          </button>

          {aiSubmitAttempts >= 3 ? (
            <p className="ai-submit-info error">
              Maximum 3 attempts reached. Close and reopen the preview to reset.
            </p>
          ) : !hasAnyComment ? (
            <p className="ai-submit-info">
              Add a per-question comment or fill the Master Instruction box to enable submission
            </p>
          ) : (
            <p className="ai-submit-info">
              {3 - aiSubmitAttempts} attempt{3 - aiSubmitAttempts !== 1 ? "s" : ""} remaining — send all your comments in one batch for best results
            </p>
          )}
        </div>
      </div>
    );
  };

  // ─── AI Review Changes modal ──────────────────────────────────────────────────
  const renderReviewModal = () => {
    const pendingCount = aiChanges.filter((c) => c.status === "pending").length;
    const acceptedCount = aiChanges.filter((c) => c.status === "accepted").length;
    const rejectedCount = aiChanges.filter((c) => c.status === "rejected").length;

    return (
      <Modal
        isOpen={showReviewModal}
        onRequestClose={() => setShowReviewModal(false)}
        className="all-lists-modal"
        overlayClassName="all-lists-overlay"
        ariaHideApp={false}
      >
        <div className="all-modal-header">
          <div className="modal-header-left">
            <h2>
              <FaRobot style={{ marginRight: "10px", color: "#7c3aed" }} />
              Review AI Changes ({aiChanges.length})
            </h2>
          </div>
          <div className="modal-header-center">
            <div className="header-actions">
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                {acceptedCount} accepted · {rejectedCount} rejected · {pendingCount} pending
              </span>
            </div>
          </div>
          <div className="modal-header-right">
            <button className="all-close-button" onClick={() => setShowReviewModal(false)}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="ai-review-toolbar">
          <button
            onClick={acceptAllChanges}
            style={{
              padding: "8px 20px",
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <FaCheck /> Accept All
          </button>
          <button
            onClick={rejectAllChanges}
            style={{
              padding: "8px 20px",
              backgroundColor: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <FaTimes /> Reject All
          </button>
        </div>

        <div className="lists-container">
          {aiChanges.map((change) => {
            const isAccepted = change.status === "accepted";
            const isRejected = change.status === "rejected";
            const isDeletion = change.updated?._deleted === true;
            const isNewQ = change.isNew === true;

            const acceptLabel = isDeletion ? "🗑 Accept Deletion"
              : isNewQ ? "➕ Add Question"
                : "✓ Accept";
            const rejectLabel = isDeletion ? "↩ Keep Question"
              : isNewQ ? "✗ Skip"
                : "✗ Reject";

            const headerBg = isAccepted ? "#ecfdf5"
              : isRejected ? "#fef2f2"
                : isDeletion ? "#fff7ed"
                  : isNewQ ? "#f0fdf4"
                    : "#f9fafb";

            const borderColor = isAccepted ? "#10b981"
              : isRejected ? "#ef4444"
                : isNewQ ? "#059669"
                  : "#e5e7eb";

            return (
              <div key={`${change.questionKey}_${change.isNew ? "new" : "mod"}`} className="list-card">
                <div className="list-header" style={{ backgroundColor: headerBg }}>
                  <div className="list-title-container">
                    <div>
                      <h3 className="list-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span>{change.questionKey}</span>
                        {!isNewQ && change.original?.Qtitle && (
                          <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: 400 }}
                            dangerouslySetInnerHTML={{
                              __html: (change.original.Qtitle || "").replace(/<[^>]+>/g, "").substring(0, 60),
                            }}
                          />
                        )}
                        {isNewQ && (change.insertAfterKey || change.insertBeforeKey) && (
                          <span style={{ fontSize: "11px", color: "#0369a1", backgroundColor: "#e0f2fe", padding: "2px 10px", borderRadius: "10px", fontWeight: 600 }}>
                            {change.insertAfterKey
                              ? `➕ Insert after ${change.insertAfterKey}`
                              : `➕ Insert before ${change.insertBeforeKey}`}
                          </span>
                        )}
                      </h3>
                      <div className="list-meta">
                        {isNewQ && !isAccepted && !isRejected && (
                          <span className="list-type-badge rows">✨ New Question</span>
                        )}
                        {isDeletion && !isAccepted && !isRejected && (
                          <span className="list-type-badge mixed">🗑 Deletion Proposed</span>
                        )}
                        {isAccepted && (
                          <span className="list-type-badge" style={{ background: "#10b981", color: "white" }}>
                            <FaCheck style={{ marginRight: "4px" }} />
                            {isDeletion ? "Deletion Accepted" : isNewQ ? "Will Be Added" : "Accepted"}
                          </span>
                        )}
                        {isRejected && (
                          <span className="list-type-badge" style={{ background: "#ef4444", color: "white" }}>
                            <FaTimes style={{ marginRight: "4px" }} />
                            {isDeletion ? "Kept" : isNewQ ? "Skipped" : "Rejected"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => acceptChange(change.questionKey)}
                      style={{
                        padding: "6px 16px",
                        backgroundColor: isAccepted ? "#10b981" : "white",
                        color: isAccepted ? "white" : "#10b981",
                        border: `1px solid ${isAccepted ? "#10b981" : "#6ee7b7"}`,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      {acceptLabel}
                    </button>
                    <button
                      onClick={() => rejectChange(change.questionKey)}
                      style={{
                        padding: "6px 16px",
                        backgroundColor: isRejected ? "#ef4444" : "white",
                        color: isRejected ? "white" : "#ef4444",
                        border: `1px solid ${isRejected ? "#ef4444" : "#fca5a5"}`,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      {rejectLabel}
                    </button>
                  </div>
                </div>

                <div className="list-content">
                  {isDeletion ? (
                    <div>
                      <div style={{ padding: "8px 14px", backgroundColor: "#fef2f2", borderRadius: "6px", marginBottom: "12px" }}>
                        <strong style={{ color: "#dc2626" }}>🗑️ Question to be deleted</strong>
                        <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#dc2626" }}>
                          Accepting will permanently remove this question from the questionnaire.
                        </p>
                      </div>
                      <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "280px", overflowY: "auto", backgroundColor: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {JSON.stringify(change.original, null, 2)}
                      </pre>
                    </div>
                  ) : isNewQ ? (
                    <div>
                      <div style={{ padding: "8px 14px", backgroundColor: "#f0fdf4", borderRadius: "6px", marginBottom: "12px" }}>
                        <strong style={{ color: "#059669" }}>✨ Proposed new question</strong>
                        <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#059669" }}>
                          Review the JSON below before accepting.
                        </p>
                      </div>
                      <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "380px", overflowY: "auto", backgroundColor: "#f8fff8", border: "1px solid #6ee7b7", borderRadius: "6px", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {JSON.stringify(change.updated, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="ai-diff-grid">
                      <div>
                        <div style={{ padding: "8px 14px", backgroundColor: "#fef2f2", borderRadius: "6px", marginBottom: "8px" }}>
                          <strong style={{ color: "#dc2626" }}>Original</strong>
                        </div>
                        <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "320px", overflowY: "auto", backgroundColor: "#fff8f8", border: "1px solid #fca5a5", borderRadius: "6px", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {JSON.stringify(change.original, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <div style={{ padding: "8px 14px", backgroundColor: "#f0fdf4", borderRadius: "6px", marginBottom: "8px" }}>
                          <strong style={{ color: "#059669" }}>AI Proposed</strong>
                        </div>
                        <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "320px", overflowY: "auto", backgroundColor: "#f8fff8", border: "1px solid #6ee7b7", borderRadius: "6px", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {JSON.stringify(change.updated, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="all-modal-header" style={{ borderTop: "1px solid #e5e7eb" }}>
          <div></div>
          <div className="modal-header-center">
            <button
              onClick={applySelectedChanges}
              disabled={acceptedCount === 0}
              style={{
                padding: "10px 32px",
                backgroundColor: acceptedCount === 0 ? "#d1d5db" : "#7c3aed",
                color: acceptedCount === 0 ? "#9ca3af" : "white",
                border: "none",
                borderRadius: "8px",
                cursor: acceptedCount === 0 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <FaCheck />
              Apply {acceptedCount > 0 ? `${acceptedCount} ` : ""}
              Selected Change{acceptedCount !== 1 ? "s" : ""}
            </button>
          </div>
          <div className="modal-header-right">
            <button className="all-close-button" onClick={() => setShowReviewModal(false)}>
              <FaTimes />
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <>
      <CommonButton
        label="Preview"
        title="Preview Survey"
        onClick={() => { setIsPreviewOpen(true); setAiSubmitAttempts(0); }}
      />

      {/* AI Review Changes modal */}
      {renderReviewModal()}

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
        className="all-lists-modal"
        overlayClassName="all-lists-overlay"
        ariaHideApp={false}
        style={{
          overlay: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            bottom: "auto",
            transform: "none",
            margin: "0",
            width: "95%",
            height: "95%",
            maxWidth: "1600px",
            borderRadius: "12px",
            padding: "0",
            overflow: "hidden",
          },
        }}
      >
        <div className="preview-modal">
          {/* Header */}
          <div className="preview-header">
            <h2>
              <FaEye style={{ marginRight: "10px", color: "#2563eb" }} />
              Survey Preview –{" "}
              <span>{projectName || surveyName || surveyFileName || "Untitled Survey"}</span>
            </h2>
            <div className="preview-header-actions">
              {/* Preview type selector */}
              <div className="preview-tabsv1">
                <select
                  value={previewType === "export" ? `export_${selectedExport}` : previewType}
                  onChange={(e) => handlePreviewChange(e.target.value)}
                >
                  <optgroup label="Survey Views">
                    <option value="questions"><FaList style={{ marginRight: "6px" }} />Questions</option>
                    <option value="statistics"><FaChartBar style={{ marginRight: "6px" }} />Statistics</option>
                    <option value="json"><FaCode style={{ marginRight: "6px" }} />Raw JSON</option>
                    <option value="survey"><FaHome style={{ marginRight: "6px" }} />Survey</option>
                    <option value="translation"><FaLanguage style={{ marginRight: "6px" }} />Translation</option>
                    <option value="psnst"><FaUserShield style={{ marginRight: "6px" }} />PSNST Survey</option>
                  </optgroup>
                  {Object.keys(exportedFiles).length > 0 && (
                    <optgroup label="Exported Files">
                      {Object.entries(exportedFiles).map(([type, data]) => (
                        <option key={type} value={`export_${type}`}>
                          <FaFileAlt style={{ marginRight: "6px" }} />
                          {type.toUpperCase()} ({data.extension}) –{" "}
                          {new Date(data.timestamp).toLocaleDateString()}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Document split view */}
              {questionnaireFileId && (
                <button
                  onClick={toggleSplitView}
                  disabled={isLoadingDocx}
                  className={`ph-action-btn info ${showSplitView ? "is-active" : ""}`}
                >
                  {isLoadingDocx
                    ? <><span className="loading-spinner" /> Loading...</>
                    : showSplitView
                      ? <><FaCheck /> Split View</>
                      : <><FaFileWord /> Show Doc</>}
                </button>
              )}

              {/* Undo / Redo (Raw JSON editing) */}
              {previewType === "json" && (
                <>
                  <CommonButton
                    symbol={faRotateLeft}
                    variant="secondary"
                    title="Undo (Ctrl+Z)"
                    disabled={!canUndo}
                    onClick={() => undo()}
                  />
                  <CommonButton
                    symbol={faRotateRight}
                    variant="secondary"
                    title="Redo (Ctrl+Y)"
                    disabled={!canRedo}
                    onClick={() => redo()}
                  />
                </>
              )}

              {/* Save button */}
              <button
                onClick={handleSaveSurvey}
                disabled={isSaving || !hasUnsavedChanges}
                className={`ph-action-btn info ${!isSaving ? "is-active" : ""}`}
                style={{
                  opacity: isSaving || !hasUnsavedChanges ? 0.6 : 1,
                  cursor: isSaving || !hasUnsavedChanges ? "not-allowed" : "pointer",
                }}
              >
                {isSaving
                  ? <><span className="loading-spinner" /> Saving...</>
                  : hasUnsavedChanges
                    ? <><FaSave /> Save Changes</>
                    : <><FaCheck /> Saved</>}
              </button>

              {showPopup && (
                <div className="lang-popup-overlay">
                  <div className="lang-popup-modal">
                    <div className="lang-popup-header">
                      <h3 className="lang-popup-title">
                        <FaLanguage /> Select Languages
                      </h3>
                      {!isDownloadingExcel && (
                        <button
                          onClick={() => setShowPopup(false)}
                          className="lang-popup-close"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    {isDownloadingExcel ? (
                      // ── Loading state inside popup ──
                      <div className="lang-popup-loading">
                        <span className="lang-popup-spinner" />
                        <p className="lang-popup-loading-title">
                          Translating & preparing your Excel file...
                        </p>
                        <p className="lang-popup-loading-hint">
                          This can take a moment for multiple languages
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="lang-popup-count">
                          {selectedLanguages.length} selected
                        </div>

                        <input
                          type="text"
                          value={languageSearch}
                          onChange={(e) => setLanguageSearch(e.target.value)}
                          placeholder="Search languages..."
                          className="lang-popup-search"
                        />

                        <div className="lang-popup-list">
                          {filteredLanguages.length === 0 ? (
                            <div className="lang-popup-empty">
                              No languages found
                            </div>
                          ) : (
                            filteredLanguages.map((lang) => (
                              <label key={lang.code} className="lang-popup-item">
                                <input
                                  type="checkbox"
                                  checked={selectedLanguages.includes(lang.label)}
                                  onChange={() => handleLanguageChange(lang.label)}
                                />
                                {lang.label}
                              </label>
                            ))
                          )}
                        </div>

                        <div className="lang-popup-actions">
                          <button
                            onClick={() => downloadexcelfile(selectedLanguages)}
                            disabled={selectedLanguages.length === 0}
                            className="lang-popup-btn download"
                          >
                            <FaDownload /> Download
                          </button>
                          <button onClick={() => setShowPopup(false)} className="lang-popup-btn cancel">
                            <FaTimes /> Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}


              {/* Voice */}
              <button
                onClick={toggleListening}
                className={`voice-button ${isListening ? "active" : ""}`}
                title="Voice Commands (e.g., 'bold title in question 1')"
              >
                {isListening ? <FaStop /> : <FaMicrophone />}
              </button>
              {transcript && (
                <div className="transcript-text">
                  Last: {transcript}
                </div>
              )}

              {/* Close button - same style as AllListsModal */}
              <button
                onClick={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
                className="all-close-button"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Main content */}
          <div
            ref={containerRef}
            className="preview-content-wrapper"
          >
            {/* Survey preview panel */}
            <div
              className="preview-content"
              style={{
                width: showSplitView ? `${splitRatio}%` : "100%",
                transition: isDragging ? "none" : "width 0.3s ease",
              }}
            >
              {previewType === "questions" && renderQuestionsPreview()}
              {previewType === "statistics" && renderStatisticsPreview()}
              {previewType === "json" && renderJSONPreview()}
              {previewType === "export" && renderExportPreview()}
              {previewType === "survey" && (
                <div className="survey-view-container">
                  <div className="survey-view-content">
                    {renderSurveyView(currentQuestion)}
                  </div>
                  <div className="survey-view-footer">
                    <button
                      onClick={handleBack}
                      disabled={isFirst}
                      className="survey-nav-btn back"
                    >
                      <FaArrowLeft /> Back
                    </button>
                    <span className="survey-counter">
                      {currentIndex + 1} / {Object.keys(sdataGroup).length}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={isLast}
                      className={`survey-nav-btn next ${isLast ? "end" : ""}`}
                    >
                      {isLast ? <><FaCheck /> End of Survey</> : <>Next <FaArrowRight /></>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* DOCX divider */}
            {showSplitView && (
              <div
                onMouseDown={handleMouseDown}
                className="divider-handle"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  if (!isDragging)
                    e.currentTarget.style.backgroundColor = "#e2e8f0";
                }}
              >
                <div className="divider-grip">⋮</div>
              </div>
            )}

            {/* DOCX preview panel */}
            {showSplitView && (
              <div
                className="docx-preview-panel"
                style={{
                  width: `${100 - splitRatio}%`,
                  transition: isDragging ? "none" : "width 0.3s ease",
                }}
              >
                {isDragging && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: "col-resize",
                    }}
                  />
                )}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Document Preview"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <div className="preview-empty">
                      {isLoadingDocx
                        ? <><span className="loading-spinner" style={{ width: "32px", height: "32px", borderWidth: "3px" }} /> Loading document...</>
                        : <><FaFileWord size={48} style={{ opacity: 0.3 }} /> No preview available</>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal >
    </>
  );
};

export default SurveyPreview;